import { some }                                                                                                 from 'lodash-es';
import { BuilderConfiguration,
  BuilderConfigurationElement,
  BuilderConfigurationElementOption,
  BuilderConfigurationFunction,
  BuilderElement,
  BuilderElementOption,
  BuilderFunction,
  BuilderGroup,
  BuilderGroupContent,
  XAxisDetails,
  XAxisReportLayout }                                                                                                               from '@/views/x-axis-report/report-edit/types/types';
import { ObjectWithStringKeys }                                                                                 from '@/types';
import { BackendXAxisSeriesResponseType, XAxisReportPanelGroups }                                               from '@/views/x-axis-report/report-edit/helpers/x-axis-report.variables';
import { DAY_OF_WEEK_NAMES, DaysOfTheWeek, Elements, ElementType, MONTH_NAMES, Period }                         from '@/helpers/variables';
import { generateDefaultValues, isElementLeadTime, isElementTypeDate, isGroupFilter, reOrderData, sortByOrder } from '@/views/x-axis-report/report-edit/helpers/x-axis.report-general.helpers';
import { SelectedFilter }                                                                                       from '@/components/filter-panel/filter-panel.types';
import { ElementOptionStyle }                                                                                   from '@/components/chart/chart-tooltip/chart-tooltip.types';

export const configurationContainsFunctions = (configuration: ObjectWithStringKeys<BuilderConfiguration[]>): boolean => !!configuration[ XAxisReportPanelGroups.FUNCTIONS ]?.length;

export const configurationContainsXAxisElements = (configuration: ObjectWithStringKeys<BuilderConfiguration[]>): boolean => !!configuration[ XAxisReportPanelGroups.X_AXIS ]?.length;

export const isConfigurationValid = (configuration: ObjectWithStringKeys<BuilderConfiguration[]>): boolean => configurationContainsFunctions(configuration)
  && configurationContainsXAxisElements(configuration);

export const getDataWithSelectedProperty = (data: (BuilderFunction | BuilderElementOption)[], element?: BuilderElement): (BuilderFunction | BuilderElementOption)[] => {
  if (data) {
    return data
      .map((el: BuilderFunction | BuilderElementOption) => ({ ...el, isSelected: el.isDefault }))
      .sort((prev: BuilderFunction | BuilderElementOption, next: BuilderFunction | BuilderElementOption) => {
        if (prev.name !== null) {
          if (element?.columnName === Period.MONTH) {
            const prevIndex = MONTH_NAMES.indexOf(prev.name.toLowerCase());
            const nextIndex = MONTH_NAMES.indexOf(next.name.toLowerCase());

            return prevIndex > nextIndex ? 1 : -1;
          }
          if (element?.columnName === Elements.DAY_OF_WEEK) {
            const prevIndex = DAY_OF_WEEK_NAMES.indexOf(prev.name.toLowerCase() as DaysOfTheWeek);
            const nextIndex = DAY_OF_WEEK_NAMES.indexOf(next.name.toLowerCase() as DaysOfTheWeek);

            return prevIndex > nextIndex ? 1 : -1;
          }
          return prev.name > next.name ? 1 : -1;
        }
        return 0;
      });
  }
  return [];
};


export const findEditedElement = (state: XAxisReportLayout, uuid: string): BuilderElement | undefined => (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
  .find((el: BuilderElement) => el.columnName === uuid);

export const filterOptionsWithValue = (content: BuilderGroupContent): boolean => {
  const element: BuilderElement = content as BuilderElement;

  return element.type === ElementType.VALUE
  && element.columnName !== Elements.LEAD_TIME
    ? !!element.options?.length
    : true;
};

export const unSelectAllOptions = (option: BuilderElementOption): BuilderElementOption => {
  option.isSelected = false;

  return option;
};

export const clearBuilderFunctions = (contentFunction: BuilderFunction): BuilderFunction => {
  contentFunction.isSelected = false;
  contentFunction.lineStyle = BackendXAxisSeriesResponseType.LINE;

  return contentFunction;
};

export const getSortedConfiguration = (configuration: ObjectWithStringKeys<BuilderConfiguration[]>, ascending = false): ObjectWithStringKeys<BuilderConfiguration[]> => {
  let sidePanelConfiguration: ObjectWithStringKeys<BuilderConfiguration[]> = {};

  Object.keys(configuration)
    .forEach((key: string) => {
      if (key === XAxisReportPanelGroups.FUNCTIONS) {
        sidePanelConfiguration = {
          ...sidePanelConfiguration,
          [ key ]: configuration[ key ],
        };
      } else {
        sidePanelConfiguration = {
          ...sidePanelConfiguration,
          [ key ]: (configuration[ key ] as BuilderConfigurationElement[])
            .sort((prev: BuilderConfigurationElement, next: BuilderConfigurationElement) => sortByOrder(prev, next, ascending))
            .map((element: BuilderConfigurationElement, order: number) => ({
              ...element,
              order,
            })),
        };
      }
    });

  return sidePanelConfiguration;
};

export const clearBuilderElements = (content: BuilderElement): BuilderElement => {
  let element: BuilderElement = content;

  if (element.options) {
    element.options = element.options.map(unSelectAllOptions);
  }

  if (element.value) {
    element = {
      ...element,
      ...generateDefaultValues(),
    };
  }

  return element;
};

export const getElementType = (element: (BuilderElement | BuilderConfigurationElement)): string => {
  const name: string = (element as BuilderElement)?.columnName || element.name;
  let { type } = element;

  switch (element.type) {
  case ElementType.DATE_RANGE:
    type = ElementType.RANGE;
    break;
  case ElementType.DAY:
    type = ElementType.DAY;
    break;
  default:
    break;
  }

  if (name === Elements.LEAD_TIME) {
    type = ElementType.LEAD_TIME;
  }

  return type ?? '';
};

export const generateCalculateElementConfiguration = (element: BuilderElement): Partial<BuilderConfigurationElement> => ({
  name: element.columnName,
  order: element.order ?? 0,
  type: getElementType(element),
});

export const getFiltersConfiguration = (filters: BuilderConfiguration[], selectedFilters: SelectedFilter[]): BuilderConfiguration[] => filters.map((filterConfiguration: BuilderConfiguration) => {
  const filter: BuilderConfigurationElement = filterConfiguration as BuilderConfigurationElement;

  const selectedFilter: SelectedFilter | undefined = selectedFilters
    .find((selectedFilter: SelectedFilter) => filter.name === selectedFilter.columnName);

  if (selectedFilter && selectedFilter.values.length) {
    filter.options = filter.options
      .filter((option: BuilderConfigurationElementOption) => {
        if (selectedFilter && selectedFilter.values.length) {
          if (filter.type === ElementType.RANGE || filter.type === ElementType.DAY) {
            return some(selectedFilter.values, option);
          }
          return selectedFilter.values.includes(option as string);
        }
        return option;
      });
  } else {
    filter.options = [];
  }

  return filter;
})
  .filter((filter: BuilderConfigurationElement) => filter.options.length);

export const setConfigurationOptions = (group: BuilderGroup<BuilderGroupContent[]>, groupName: XAxisReportPanelGroups, payload: XAxisDetails): BuilderGroupContent[] => (
  group.data
    .sort((a: BuilderGroupContent, b: BuilderGroupContent) => sortByOrder(a, b, true))
    .map((data: BuilderGroupContent) => {
      if (groupName === XAxisReportPanelGroups.FUNCTIONS) {
        const xAxisFunction: BuilderFunction = data as BuilderFunction;

        const aggregationFunction: BuilderConfigurationFunction | undefined = (payload[ groupName ] as BuilderConfigurationFunction[])
          .find((fnc: BuilderConfigurationFunction) => fnc.function === xAxisFunction.aggregationFunction);

        if (aggregationFunction) {
          xAxisFunction.isSelected = !!aggregationFunction;
          xAxisFunction.lineStyle = (aggregationFunction.type as string | null);
        }

        return {
          ...xAxisFunction,
          color: aggregationFunction?.color ?? null,
          label: aggregationFunction?.label || xAxisFunction.label,
          order: aggregationFunction?.order ?? null,
        };
      }
      if (payload[ groupName ]) {
        const element: BuilderElement = data as BuilderElement;
        const elementWithStyles: BuilderConfigurationElement | undefined = (payload[ groupName ] as BuilderConfigurationElement[])
          .sort(sortByOrder)
          .map(reOrderData)
          .find((e: BuilderConfigurationElement) => e.name === element.columnName);

        if (elementWithStyles) {
          const optionsWithStyles: BuilderConfigurationElementOption[] = elementWithStyles.options
            .sort((a: BuilderConfigurationElementOption, b: BuilderConfigurationElementOption) => sortByOrder(a as ElementOptionStyle, b as ElementOptionStyle));

          const from: string = isGroupFilter(groupName) ? optionsWithStyles[ 0 ] as string : (optionsWithStyles[ 0 ] as ElementOptionStyle).name;
          const to: string = isGroupFilter(groupName) ? optionsWithStyles[ optionsWithStyles.length - 1 ] as string : (optionsWithStyles[ optionsWithStyles.length - 1 ] as ElementOptionStyle).name;

          if (elementWithStyles.order != null) {
            element.order = elementWithStyles.order;
          }
          if (isElementTypeDate(element)) {
            element.value = {
              ...element.value,
              from: optionsWithStyles?.length ? from : element.value?.from || '',
              to: optionsWithStyles?.length ? to : element.value?.to || '',
            };
          } else if (isElementLeadTime(element)) {
            element.value = {
              ...element.value,
              from: optionsWithStyles?.length ? Math.abs(+from)
                .toString() : element.value?.from || '',
              to: optionsWithStyles?.length ? Math.abs(+to)
                .toString() : element.value?.to || '',
            };
          } else {
            element.order = elementWithStyles.order;
            element.options = element.options
              ?.map((opt: BuilderElementOption) => {
                const option = opt as BuilderElementOption;
                const matchedOption: ElementOptionStyle | undefined = optionsWithStyles
                  ?.find((optionWithStyle: BuilderConfigurationElementOption) => ((optionWithStyle as ElementOptionStyle)?.name
                    ? (optionWithStyle as ElementOptionStyle).name === option.name
                    : optionWithStyle as string === option.name)) as ElementOptionStyle;

                return {
                  ...option,
                  ...matchedOption,
                  color: matchedOption?.color ?? null,
                  label: matchedOption?.label || option.label,
                  isSelected: !!matchedOption,
                  order: matchedOption?.order ?? null,
                };
              })
              .sort(sortByOrder) || [];
          }
        } else {
          element.order = null;
          element.options = element.options
            ?.map((option: BuilderElementOption) => ({
              ...option,
              color: null,
              label: option.label || null,
              order: null,
            }));
        }

        return {
          ...element,
          leadElement: !!elementWithStyles?.leadElement,
        };
      }

      return data;
    })
);
